import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Users, LogIn, Lightbulb, MousePointerClick, Loader2, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_ICONS = {
  banner_click: MousePointerClick,
  auth_shown: LogIn,
  auth_success: Users,
  market_submitted: Lightbulb,
};

const STAGE_COLOR = {
  banner_click: '#F97316',
  auth_shown: '#3B82F6',
  auth_success: '#8B5CF6',
  market_submitted: '#22C55E',
};

const RANGE_OPTIONS = [
  { label: '7d', value: 7 },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];

const CreationFunnelCard = () => {
  const [days, setDays] = useState(7);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/admin/analytics/funnel?days=${days}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`Failed to load funnel (${res.status})`);
      }
      const data = await res.json();
      setReport(data);
    } catch (e) {
      setError(e.message || 'Failed to load funnel');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const topCount = report?.stages?.[0]?.count || 0;

  return (
    <div
      data-testid="creation-funnel-card"
      className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-5 mt-6"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-[#F97316]" />
          <h3 className="text-lg font-bold text-white">Market Creation Funnel</h3>
        </div>
        <div className="flex items-center gap-1.5">
          {RANGE_OPTIONS.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              onClick={() => setDays(value)}
              data-testid={`funnel-range-${label}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                days === value
                  ? 'bg-[#3B82F6] text-white'
                  : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56]'
              }`}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            onClick={fetchReport}
            disabled={loading}
            data-testid="funnel-refresh"
            aria-label="Refresh"
            className="ml-1 p-1.5 rounded-full bg-[#1e2a45] text-gray-400 hover:text-white hover:bg-[#2d3a56] transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <p className="text-red-400 text-sm mb-3" data-testid="funnel-error">
          {error}
        </p>
      )}

      {loading && !report ? (
        <div className="flex items-center justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading funnel…
        </div>
      ) : report ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {report.stages.map((s) => {
              const Icon = STAGE_ICONS[s.key] || TrendingUp;
              const color = STAGE_COLOR[s.key] || '#3B82F6';
              const widthPct = topCount > 0 ? Math.max(5, (s.count / topCount) * 100) : 0;
              return (
                <div
                  key={s.key}
                  data-testid={`funnel-stage-${s.key}`}
                  className="bg-[#050810] rounded-xl p-4 border border-[#1e2a45] relative overflow-hidden"
                >
                  <div
                    className="absolute inset-y-0 left-0 opacity-10"
                    style={{ width: `${widthPct}%`, background: color }}
                    aria-hidden="true"
                  />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="h-4 w-4" style={{ color }} />
                      <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
                        {s.label}
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-white">{s.count}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {s.conversion_from_top}% of top
                      {s.key !== 'banner_click' && (
                        <>
                          <span className="mx-1 text-gray-700">•</span>
                          {s.conversion_from_prev}% of prev
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between bg-[#050810] rounded-xl p-4 border border-[#1e2a45]">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">
                Overall conversion
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Click → submitted market, last {report.days} day{report.days === 1 ? '' : 's'}
              </p>
            </div>
            <div
              data-testid="funnel-overall-conversion"
              className="text-3xl font-extrabold text-[#22C55E]"
            >
              {report.overall_conversion}%
            </div>
          </div>

          {Array.isArray(report.sources) && report.sources.length > 0 && (
            <div className="mt-5" data-testid="funnel-sources">
              <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-2">
                By source
              </p>
              <div className="overflow-x-auto rounded-xl border border-[#1e2a45]">
                <table className="w-full text-sm">
                  <thead className="bg-[#0b1222] text-gray-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-[11px] uppercase tracking-wide">
                        Source
                      </th>
                      <th className="text-right px-3 py-2 font-semibold text-[11px] uppercase tracking-wide">
                        Clicks
                      </th>
                      <th className="text-right px-3 py-2 font-semibold text-[11px] uppercase tracking-wide">
                        Auth shown
                      </th>
                      <th className="text-right px-3 py-2 font-semibold text-[11px] uppercase tracking-wide">
                        Logged in
                      </th>
                      <th className="text-right px-3 py-2 font-semibold text-[11px] uppercase tracking-wide">
                        Submitted
                      </th>
                      <th className="text-right px-3 py-2 font-semibold text-[11px] uppercase tracking-wide">
                        Conv.
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.sources.map((s) => (
                      <tr
                        key={s.source}
                        data-testid={`funnel-source-row-${s.source}`}
                        className="border-t border-[#1e2a45] hover:bg-[#0b1222]"
                      >
                        <td className="px-3 py-2 text-white font-medium">
                          {s.source === 'unknown' ? (
                            <span className="text-gray-500 italic">unknown</span>
                          ) : (
                            s.source
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-200">{s.banner_click}</td>
                        <td className="px-3 py-2 text-right text-gray-200">{s.auth_shown}</td>
                        <td className="px-3 py-2 text-right text-gray-200">{s.auth_success}</td>
                        <td className="px-3 py-2 text-right text-gray-200">{s.market_submitted}</td>
                        <td className="px-3 py-2 text-right font-semibold text-[#22C55E]">
                          {s.conversion}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default CreationFunnelCard;
